import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  LinearProgress,
  makeStyles,
  Modal,
  Paper,
  Radio,
  RadioGroup,
  Slide,
  TextField,
  Theme,
  Tooltip,
  Typography
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ListAltIcon from "@material-ui/icons/ListAlt";
import TimerIcon from "@material-ui/icons/Timer";
import WarningIcon from "@material-ui/icons/Warning";
import managerTestApi from "api/managerTest";
import { useAppSelector } from "app/hooks";
import clsx from "clsx";
import { selectInfoLogin } from "features/Auth/authSlice";
import { AnswerContent, ExamQuestions, ListExam, ResponseSubmissionExam, StudentAnswers } from "models/managerTest";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { scroller } from "react-scroll";
import { toast } from "react-toastify";
import { dateTimeToFormatYYYY } from "utils/dateTimeFormat";

export default function BeginExam() {
  const useStyles = makeStyles((theme: Theme) => ({
    container: {
      padding: "20px 24px",
      maxWidth: 1440,
      margin: "0 auto",
      width: "100%",
      [theme.breakpoints?.down("sm")]: {
        padding: "10px 8px",
      },
    },
    header: {
      background: "linear-gradient(135deg, #0F6299 0%, #137BBD 60%, #2563EB 100%)",
      borderRadius: 16,
      color: "#FFFFFF",
      boxShadow: "0 10px 30px -5px rgba(19, 123, 189, 0.25)",
      padding: "16px 24px",
      marginBottom: 24,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 16,
    },
    headerInfo: {
      display: "flex",
      flexDirection: "column",
      gap: 4,
    },
    headerTitle: {
      fontSize: 20,
      fontWeight: 700,
      color: "#FFFFFF",
      [theme.breakpoints?.down("sm")]: {
        fontSize: 15,
      },
    },
    headerSubtitle: {
      fontSize: 13,
      color: "#E0F2FE",
      fontWeight: 500,
    },
    headerTimer: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      backgroundColor: "rgba(255, 255, 255, 0.95)",
      color: "#DC2626",
      padding: "8px 20px",
      borderRadius: 12,
      fontSize: 22,
      fontWeight: 800,
      boxShadow: "0 4px 14px rgba(0, 0, 0, 0.15)",
      letterSpacing: "0.05em",
      [theme.breakpoints?.down("sm")]: {
        fontSize: 16,
        padding: "6px 12px",
      },
    },
    sidebarCard: {
      backgroundColor: "#FFFFFF",
      borderRadius: 16,
      border: "1px solid #E2E8F0",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.04)",
      padding: "20px",
      position: "sticky",
      top: 90,
      maxHeight: "calc(100vh - 120px)",
      overflowY: "auto",
      [theme.breakpoints?.down("sm")]: {
        display: "none",
      },
    },
    desktopSidebarGrid: {
      [theme.breakpoints?.down("sm")]: {
        display: "none",
      },
    },
    mobileFloatingTopNav: {
      display: "none",
      [theme.breakpoints?.down("sm") || "@media (max-width:960px)"]: {
        display: "block",
        position: "sticky",
        top: 58,
        zIndex: 950,
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(12px)",
        borderRadius: 16,
        border: "1px solid #CBD5E1",
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.1)",
        padding: "10px 14px",
        marginTop: 12,
        marginBottom: 16,
        transition: "border-radius 0.2s ease, box-shadow 0.2s ease",
      },
    },
    mobileFloatingTopNavSticky: {
      [theme.breakpoints?.down("sm") || "@media (max-width:960px)"]: {
        borderRadius: "0 0 16px 16px !important",
        borderTop: "none !important",
        boxShadow: "0 10px 30px rgba(15, 23, 42, 0.12) !important",
      },
    },
    mobileBadgeScrollContainer: {
      display: "flex",
      alignItems: "center",
      overflowX: "auto",
      gap: 8,
      padding: "8px 4px",
      minHeight: 52,
      WebkitOverflowScrolling: "touch",
      scrollbarWidth: "none",
      "&::-webkit-scrollbar": {
        display: "none",
      },
    },
    sidebarTitle: {
      fontWeight: 700,
      color: "#0F172A",
      fontSize: "0.95rem",
      display: "flex",
      alignItems: "center",
      gap: 8,
      marginBottom: 14,
    },
    progressBox: {
      marginBottom: 16,
    },
    progressStats: {
      display: "flex",
      justifyContent: "space-between",
      fontSize: 13,
      fontWeight: 600,
      color: "#475569",
      marginBottom: 6,
    },
    questionGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(40px, 1fr))",
      gap: 8,
      marginTop: 12,
      marginBottom: 16,
      padding: "6px 4px",
    },
    questionBadge: {
      minWidth: 40,
      height: 40,
      borderRadius: 10,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: 700,
      fontSize: 13,
      cursor: "pointer",
      transition: "all 0.2s ease-in-out",
      userSelect: "none",
      flexShrink: 0,
      boxSizing: "border-box",
      "&:hover": {
        transform: "translateY(-2px)",
      },
    },
    questionBadgeAnswered: {
      backgroundColor: "#ECFDF5",
      color: "#065F46",
      border: "1px solid #A7F3D0",
    },
    questionBadgeUnanswered: {
      backgroundColor: "#F1F5F9",
      color: "#64748B",
      border: "1px solid #CBD5E1",
      "&:hover": {
        backgroundColor: "#E2E8F0",
        color: "#0F172A",
      },
    },
    questionBadgeActive: {
      boxShadow: "0 0 0 2px #2563EB, 0 4px 12px rgba(37, 99, 235, 0.35)",
      backgroundColor: "#2563EB !important",
      color: "#FFFFFF !important",
      border: "1px solid #1D4ED8 !important",
      transform: "translateY(-2px)",
      fontWeight: 800,
      zIndex: 2,
    },
    legendBox: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-around",
      paddingTop: 12,
      borderTop: "1px solid #F1F5F9",
      fontSize: 12,
      color: "#64748B",
      fontWeight: 500,
    },
    legendItem: {
      display: "flex",
      alignItems: "center",
      gap: 6,
    },
    legendDot: {
      width: 12,
      height: 12,
      borderRadius: 4,
    },
    examCard: {
      backgroundColor: "#FFFFFF",
      borderRadius: 16,
      border: "1px solid #E2E8F0",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.04)",
      padding: "24px",
      marginBottom: 20,
      transition: "border-color 0.2s ease, box-shadow 0.2s ease",
      "&:hover": {
        borderColor: "#93C5FD",
        boxShadow: "0 8px 20px rgba(19, 123, 189, 0.08)",
      },
    },
    questionNumberHeader: {
      display: "flex",
      alignItems: "flex-start",
      gap: 12,
      marginBottom: 16,
    },
    questionNumberBadge: {
      backgroundColor: "#EEF2FF",
      color: "#1D4ED8",
      fontWeight: 800,
      fontSize: 14,
      padding: "4px 12px",
      borderRadius: 8,
      whiteSpace: "nowrap",
    },
    questionContent: {
      fontSize: 16,
      fontWeight: 600,
      color: "#0F172A",
      lineHeight: 1.5,
    },
    formAnswers: {
      marginTop: 12,
      display: "flex",
      flexDirection: "column",
      gap: 10,
    },
    answerOptionBox: {
      display: "flex",
      alignItems: "center",
      padding: "10px 16px",
      borderRadius: 12,
      border: "1px solid #E2E8F0",
      transition: "all 0.2s ease",
      cursor: "pointer",
      "&:hover": {
        backgroundColor: "#F8FAFC",
        borderColor: "#CBD5E1",
      },
    },
    answerOptionBoxSelected: {
      backgroundColor: "#F0F9FF",
      borderColor: "#38BDF8",
      boxShadow: "0 2px 8px rgba(56, 189, 248, 0.15)",
    },
    answerContent: {
      fontSize: 14,
      color: "#334155",
      fontWeight: 500,
      marginLeft: 8,
    },
    btnSubmit: {
      background: "linear-gradient(135deg, #10BCA7 0%, #059669 100%)",
      borderRadius: 12,
      padding: "12px 36px",
      color: "#FFFFFF",
      fontWeight: 700,
      fontSize: 16,
      textTransform: "none",
      boxShadow: "0 4px 14px rgba(16, 188, 167, 0.3)",
      transition: "all 0.25s ease",
      "&:hover": {
        background: "linear-gradient(135deg, #0D9488 0%, #047857 100%)",
        boxShadow: "0 6px 18px rgba(16, 188, 167, 0.4)",
        transform: "translateY(-1px)",
      },
    },
    iconScroll: {
      position: "fixed",
      right: 30,
      bottom: 25,
      display: "flex",
      flexDirection: "column",
      gap: 8,
      zIndex: 99,
      [theme.breakpoints?.down("sm")]: {
        right: 15,
        bottom: 15,
      },
    },
    scrollFab: {
      width: 44,
      height: 44,
      minWidth: 44,
      minHeight: 44,
      borderRadius: "50%",
      backgroundColor: "#FFFFFF",
      color: "#2563EB",
      border: "1px solid #E2E8F0",
      boxShadow: "0 4px 14px rgba(15, 23, 42, 0.15)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: "#2563EB",
        color: "#FFFFFF",
        borderColor: "#1D4ED8",
        transform: "scale(1.1)",
        boxShadow: "0 6px 18px rgba(37, 99, 235, 0.35)",
      },
    },
    rootModal: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 20,
    },
    modalContainer: {
      maxWidth: 480,
      width: "100%",
      borderRadius: 16,
      backgroundColor: "#FFFFFF",
      boxShadow: "0 20px 40px rgba(0, 0, 0, 0.2)",
      overflow: "hidden",
    },
    modalHeader: {
      background: "linear-gradient(135deg, #DC2626 0%, #EF4444 100%)",
      padding: "16px 20px",
      color: "#FFFFFF",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    modalContent: {
      padding: 24,
      textAlign: "center",
    },
  }));

  const classes = useStyles();
  const { t } = useTranslation();
  const location: any = useLocation();
  const navigate = useNavigate();
  const infoUser = useAppSelector(selectInfoLogin);
  const [detailTest, setDetailTest] = useState<ListExam>();
  const [studentAnswers, setStudentAnswers] = useState<StudentAnswers[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [openModal, setOpenModal] = useState(false);
  const [disabledSubmit, setDisabledSubmit] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(false);
  const [isRestored, setIsRestored] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const targetParents: HTMLElement[] = [];
    const originalOverflows: string[] = [];

    if (containerRef.current) {
      let parent = containerRef.current.parentElement;
      while (parent) {
        const computedStyle = window.getComputedStyle(parent);
        if (
          computedStyle.overflow === "hidden" ||
          computedStyle.overflowX === "hidden" ||
          computedStyle.overflowY === "hidden"
        ) {
          targetParents.push(parent);
          originalOverflows.push(parent.style.overflow);
          parent.style.overflow = "visible";
        }
        parent = parent.parentElement;
      }
    }

    return () => {
      targetParents.forEach((parent, index) => {
        if (parent) {
          parent.style.overflow = originalOverflows[index];
        }
      });
    };
  }, [detailTest]);

  // Manage active exam session (resilient to browser reload)
  const examIdParam = location?.state?.id;
  const rawStartTimeParam = location?.state?.startTime;

  const currentExamId = (() => {
    if (examIdParam) {
      localStorage.setItem("active_exam_id", String(examIdParam));
      return Number(examIdParam);
    }
    const saved = localStorage.getItem("active_exam_id");
    return saved ? Number(saved) : 0;
  })();

  const startTime = (() => {
    if (rawStartTimeParam) {
      const formatted =
        moment(rawStartTimeParam).format("YYYY/MM/DD").split("/").join("") +
        moment(rawStartTimeParam).format("HH:mm:ss").split(":").join("");
      localStorage.setItem("active_exam_starttime", formatted);
      return formatted;
    }
    return localStorage.getItem("active_exam_starttime") || moment(new Date()).format("YYYYMMDDHHmmss");
  })();

  // STABLE STORAGE KEYS per exam (prevents re-triggering useEffect when user object loads)
  const storageKey = `gpet_exam_answers_${currentExamId}`;
  const orderStorageKey = `gpet_exam_question_order_${currentExamId}`;
  const answerOrderStorageKey = `gpet_exam_answer_order_${currentExamId}`;
  const violationStorageKey = `gpet_exam_violation_${currentExamId}`;

  const [violationCount, setViolationCount] = useState<number>(() => {
    const saved = localStorage.getItem(violationStorageKey);
    return saved ? parseInt(saved, 10) : 0;
  });
  const [openViolationModal, setOpenViolationModal] = useState<boolean>(false);
  const lastViolationTimeRef = useRef<number>(0);

  // Theo dõi hành vi rời tab / chuyển cửa sổ của học viên
  useEffect(() => {
    if (!currentExamId || loading || disabledSubmit) return;

    const handleTabViolation = () => {
      const now = Date.now();
      if (now - lastViolationTimeRef.current < 1500) return; // Debounce 1.5s
      lastViolationTimeRef.current = now;

      setViolationCount((prev) => {
        const next = prev + 1;
        localStorage.setItem(violationStorageKey, String(next));
        managerTestApi
          .logViolation({
            examId: currentExamId,
            violationCount: next,
            violationType: "TAB_SWITCH",
          })
          .catch(() => {});
        return next;
      });
      setOpenViolationModal(true);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        handleTabViolation();
      }
    };

    const handleBlur = () => {
      handleTabViolation();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleBlur);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleBlur);
    };
  }, [currentExamId, loading, disabledSubmit, violationStorageKey]);

  // Fetch exam details & restore saved question order & saved answers from localStorage
  useEffect(() => {
    (async () => {
      try {
        if (!currentExamId) {
          toast.error(t("test.chuaden_gio_thi") || "Không tìm thấy thông tin bài thi");
          navigate("/exam-upcoming");
          return;
        }

        const res: ListExam = await managerTestApi.getDetailExam(currentExamId, true);
        if (res.responseCode === "20" || !res.exam?.examQuestions?.length) {
          toast.error(t("test.chuaden_gio_thi") || "Chưa đến giờ thi hoặc đã hết thời gian làm bài");
          navigate("/exam-upcoming");
          return;
        }

        // 1. Freeze Question Order across page reloads
        const savedQuestionOrder = localStorage.getItem(orderStorageKey);
        if (savedQuestionOrder) {
          try {
            const orderIds: number[] = JSON.parse(savedQuestionOrder);
            if (Array.isArray(orderIds) && orderIds.length > 0) {
              res.exam.examQuestions.sort((a: ExamQuestions, b: ExamQuestions) => {
                const idxA = orderIds.indexOf(a.questionId);
                const idxB = orderIds.indexOf(b.questionId);
                if (idxA !== -1 && idxB !== -1) return idxA - idxB;
                return 0;
              });
            }
          } catch (e) {
            console.error("Error parsing question order", e);
          }
        } else {
          const newOrder = res.exam.examQuestions.map((q: ExamQuestions) => q.questionId);
          localStorage.setItem(orderStorageKey, JSON.stringify(newOrder));
        }

        // 2. Freeze Options/Answers Order per Question across page reloads
        const savedAnswerOrder = localStorage.getItem(answerOrderStorageKey);
        if (savedAnswerOrder) {
          try {
            const answerOrderMap: Record<number, number[]> = JSON.parse(savedAnswerOrder);
            res.exam.examQuestions.forEach((eq: ExamQuestions) => {
              const savedOptionIds = answerOrderMap[eq.questionId];
              if (savedOptionIds && Array.isArray(savedOptionIds) && eq.question.answers) {
                eq.question.answers.sort((a: AnswerContent, b: AnswerContent) => {
                  const idxA = a.id != null ? savedOptionIds.indexOf(Number(a.id)) : -1;
                  const idxB = b.id != null ? savedOptionIds.indexOf(Number(b.id)) : -1;
                  if (idxA !== -1 && idxB !== -1) return idxA - idxB;
                  return 0;
                });
              }
            });
          } catch (e) {
            console.error("Error parsing answer order", e);
          }
        } else {
          const answerOrderMap: Record<number, number[]> = {};
          res.exam.examQuestions.forEach((eq: ExamQuestions) => {
            if (eq.question.answers) {
              answerOrderMap[eq.questionId] = eq.question.answers
                .map((a: AnswerContent) => (a.id != null ? Number(a.id) : 0))
                .filter(Boolean);
            }
          });
          localStorage.setItem(answerOrderStorageKey, JSON.stringify(answerOrderMap));
        }

        setDetailTest(res);

        const defaultAnswers = res.exam.examQuestions.map((question: ExamQuestions) => ({
          questionId: question.questionId,
        }));

        // 3. Restore saved student answers keyed by questionId
        const savedData = localStorage.getItem(storageKey);
        if (savedData) {
          try {
            const parsed: StudentAnswers[] = JSON.parse(savedData);
            if (Array.isArray(parsed) && parsed.length > 0) {
              const restoredAnswers = defaultAnswers.map((defItem: StudentAnswers) => {
                const match = parsed.find((p) => Number(p.questionId) === Number(defItem.questionId));
                return match ? { ...defItem, ...match } : defItem;
              });
              setStudentAnswers(restoredAnswers);
              setIsRestored(true);
              setLoading(false);
              return;
            }
          } catch (e) {
            console.error("Error parsing saved exam answers", e);
          }
        }
        setStudentAnswers(defaultAnswers);
        setIsRestored(true);
      } catch (error: any) {
        toast.error(error?.message || t("test.chuaden_gio_thi") || "Không thể tải đề thi");
        navigate("/exam-upcoming");
      } finally {
        setLoading(false);
      }
    })();
  }, [currentExamId, storageKey, orderStorageKey, answerOrderStorageKey]);

  // Automatically sync studentAnswers to localStorage whenever updated
  useEffect(() => {
    if (isRestored && currentExamId && studentAnswers.length > 0) {
      localStorage.setItem(storageKey, JSON.stringify(studentAnswers));
    }
  }, [studentAnswers, currentExamId, storageKey, isRestored]);

  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    if (hours === 0 && minutes === 0 && seconds === 1) {
      setOpenModal(true);
    }
  }, [hours, minutes, seconds]);

  useEffect(() => {
    const interval = setInterval(() => {
      const timer = detailTest?.exam?.examTime;
      if (!timer) return;
      const currentDay = new Date().setMinutes(new Date().getMinutes() - timer);
      const examTimer = new Date(detailTest?.exam?.startDate).getTime();
      const difference = moment.duration(moment(examTimer).diff(moment(currentDay)));
      if (difference.milliseconds() < 0) {
        return;
      }
      if (difference.hours() <= Math.floor(timer / 60) && difference.hours() >= 0) {
        setHours(difference.hours());
      }
      setMinutes(difference.minutes() >= 0 ? difference.minutes() : 0);
      setSeconds(difference.seconds() >= 0 ? difference.seconds() : 0);
    }, 1000);
    return () => clearInterval(interval);
  }, [detailTest]);

  const getAnswerForQuestion = (questionId: number | undefined, index: number) => {
    if (questionId) {
      const found = studentAnswers.find((sa) => Number(sa.questionId) === Number(questionId));
      if (found) return found;
    }
    return studentAnswers[index];
  };

  const isQuestionAnswered = (index: number) => {
    const question = detailTest?.exam?.examQuestions?.[index];
    const ans = getAnswerForQuestion(question?.questionId, index);
    if (!ans) return false;
    if (detailTest?.exam?.type === "TYPING") {
      return Boolean(ans.typing && ans.typing.trim().length > 0);
    }
    return ans.choice != null && ans.choice !== undefined && ans.choice !== "";
  };

  const answeredCount = (detailTest?.exam?.examQuestions || []).filter((_: any, idx: number) =>
    isQuestionAnswered(idx)
  ).length;
  const totalQuestions = detailTest?.exam?.examQuestions?.length || 0;
  const progressPercent = totalQuestions > 0 ? (answeredCount / totalQuestions) * 100 : 0;

  const handleChangeRadio = (
    event: React.ChangeEvent<HTMLInputElement>,
    questionId: number | undefined,
    index: number
  ) => {
    const selectedChoice = Number(event.target.value);
    setStudentAnswers((prev) => {
      const next = [...prev];
      const targetIndex = next.findIndex((sa) => Number(sa.questionId) === Number(questionId));
      if (targetIndex !== -1) {
        next[targetIndex] = { ...next[targetIndex], choice: selectedChoice, questionId };
      } else if (next[index]) {
        next[index] = { ...next[index], choice: selectedChoice, questionId };
      } else {
        next.push({ questionId, choice: selectedChoice } as StudentAnswers);
      }
      localStorage.setItem(storageKey, JSON.stringify(next));
      return next;
    });
  };

  const handleChangeOptionValue = (value: string, questionId: number | undefined, index: number) => {
    setStudentAnswers((prev) => {
      const next = [...prev];
      const targetIndex = next.findIndex((sa) => Number(sa.questionId) === Number(questionId));
      if (targetIndex !== -1) {
        next[targetIndex] = { ...next[targetIndex], typing: value, questionId };
      } else if (next[index]) {
        next[index] = { ...next[index], typing: value, questionId };
      } else {
        next.push({ questionId, typing: value } as StudentAnswers);
      }
      localStorage.setItem(storageKey, JSON.stringify(next));
      return next;
    });
  };

  const [activeQuestionIndex, setActiveQuestionIndex] = useState<number>(0);
  const [isStickyMobile, setIsStickyMobile] = useState<boolean>(false);

  useEffect(() => {
    const handleScroll = () => {
      const sentinel = document.getElementById("sticky-sentinel");
      if (sentinel) {
        const rect = sentinel.getBoundingClientRect();
        setIsStickyMobile(rect.top <= 76);
      }

      const questions = detailTest?.exam?.examQuestions || [];
      for (let i = questions.length - 1; i >= 0; i--) {
        const el = document.getElementById(`question-card-${i}`);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 250) {
            setActiveQuestionIndex(i);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [detailTest]);

  useEffect(() => {
    const container = document.getElementById("mobile-badge-container");
    const activeBadge = document.getElementById(`mobile-badge-${activeQuestionIndex}`);
    if (container && activeBadge) {
      const badgeOffsetLeft = activeBadge.offsetLeft;
      const badgeWidth = activeBadge.offsetWidth;
      const containerWidth = container.offsetWidth;
      container.scrollTo({
        left: badgeOffsetLeft - containerWidth / 2 + badgeWidth / 2,
        behavior: "smooth",
      });
    }
  }, [activeQuestionIndex]);

  const handleSelectQuestion = (index: number) => {
    setActiveQuestionIndex(index);
    const element = document.getElementById(`question-card-${index}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleScrollToTop = () => {
    scroller.scrollTo("header", {
      duration: 600,
      smooth: "easeInOutQuart",
      offset: -80,
    });
  };

  const handleScrollToBottom = () => {
    scroller.scrollTo("footer", {
      duration: 600,
      smooth: "easeInOutQuart",
      offset: -80,
    });
  };

  const handleSubmitExam = async () => {
    setDisabledSubmit(true);
    try {
      const res: ResponseSubmissionExam = await managerTestApi.submitTest({
        examId: detailTest?.exam?.id,
        startTime,
        endTime:
          moment(new Date()).format("YYYY/MM/DD").split("/").join("") +
          moment(new Date()).format("HH:mm:ss").split(":").join(""),
        studentAnswers: studentAnswers.map((answer) => ({
          questionId: answer.questionId,
          choice: answer.choice != null && answer.choice !== "" ? Number(answer.choice) : null,
          typing: answer.typing,
        })),
      });
      setDisabledSubmit(false);

      if (res.responseCode === "00") {
        localStorage.removeItem(storageKey);
        localStorage.removeItem(orderStorageKey);
        localStorage.removeItem(answerOrderStorageKey);
        localStorage.removeItem("active_exam_id");
        localStorage.removeItem("active_exam_starttime");
        localStorage.removeItem(violationStorageKey);
        toast.success(`${t("test.nopbaithanhcong")}`);
        navigate(`/exam/result-exam/${detailTest?.exam?.id}`, {
          state: {
            res,
            type: detailTest?.exam?.type,
          },
        });
      } else if (res.responseDescription === "You have finished this exam.") {
        localStorage.removeItem(storageKey);
        localStorage.removeItem(orderStorageKey);
        localStorage.removeItem(answerOrderStorageKey);
        localStorage.removeItem("active_exam_id");
        localStorage.removeItem("active_exam_starttime");
        localStorage.removeItem(violationStorageKey);
        toast.error(`${t("test.bandanopbai")}`);
        throw new Error(res.responseDescription);
      } else {
        toast.error(res.responseDescription);
      }
    } catch (error: any) {
      setDisabledSubmit(false);
      toast.error(error?.message || `${t("test.nopbaithatbai")}`);
    }
  };

  if (!currentExamId) {
    return <Navigate to="/home" />;
  }

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="70vh"
        style={{ gap: 16 }}
      >
        <CircularProgress style={{ color: "#2563EB" }} size={48} />
        <Typography style={{ color: "#475569", fontWeight: 600, fontSize: "0.95rem" }}>
          Đang tải đề thi và câu hỏi, vui lòng chờ trong giây lát...
        </Typography>
      </Box>
    );
  }

  return (
    <div ref={containerRef}>
      <Box className={classes.container}>
      {/* Mobile Floating Sticky Question Navigation Bar */}
      <div id="sticky-sentinel" style={{ height: 1, marginTop: -1 }} />
      <Box
        id="mobile-floating-top-nav"
        className={clsx(
          classes.mobileFloatingTopNav,
          isStickyMobile && classes.mobileFloatingTopNavSticky
        )}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography style={{ fontSize: 13, fontWeight: 700, color: "#0F172A" }}>
            {t("test.danhsachcauhoi")} ({answeredCount}/{totalQuestions})
          </Typography>
          <Typography style={{ fontSize: 12, fontWeight: 600, color: "#64748B" }}>
            Chạm để chuyển câu
          </Typography>
        </Box>
        <div id="mobile-badge-container" className={classes.mobileBadgeScrollContainer}>
          {detailTest?.exam?.examQuestions?.map((_: any, index: number) => {
            const answered = isQuestionAnswered(index);
            const isActive = activeQuestionIndex === index;

            return (
              <div
                key={index}
                id={`mobile-badge-${index}`}
                className={clsx(
                  classes.questionBadge,
                  answered ? classes.questionBadgeAnswered : classes.questionBadgeUnanswered,
                  isActive && classes.questionBadgeActive
                )}
                onClick={() => handleSelectQuestion(index)}
              >
                {index + 1}
              </div>
            );
          })}
        </div>
      </Box>

      {/* Header Banner */}
      <Box className={classes.header} id="header">
        <Box className={classes.headerInfo}>
          <Typography className={classes.headerTitle}>
            {detailTest?.exam?.examTitle || t("test.baikiemtra")}
          </Typography>
          <Typography className={classes.headerSubtitle}>
            {t("test.hocvien")}: <strong>{infoUser.name}</strong> • {t("test.ngaylambai")}: {dateTimeToFormatYYYY(detailTest?.exam?.startDate)}
          </Typography>
        </Box>
        <Box style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <Box className={classes.headerTimer}>
            <TimerIcon style={{ fontSize: 24 }} />
            <span>
              {hours < 10 ? "0" + hours : hours}:{minutes < 10 ? "0" + minutes : minutes}:
              {seconds < 10 ? "0" + seconds : seconds}
            </span>
          </Box>

          <Button
            variant="contained"
            disabled={disabledSubmit}
            onClick={handleSubmitExam}
            style={{
              backgroundColor: "#10BCA7",
              color: "#FFFFFF",
              fontWeight: 700,
              borderRadius: 12,
              padding: "10px 24px",
              fontSize: 15,
              textTransform: "none",
              boxShadow: "0 4px 14px rgba(16, 188, 167, 0.4)",
            }}
          >
            {t("test.nopbai")}
          </Button>
        </Box>
      </Box>

      {/* Main Grid: Sidebar Left + Exam Right */}
      <Grid container spacing={3}>
        {/* Left Sidebar Navigation Panel (Desktop Only) */}
        <Grid item xs={12} md={4} lg={3} className={classes.desktopSidebarGrid}>
          <Paper className={classes.sidebarCard} elevation={0}>
            <Typography className={classes.sidebarTitle}>
              <ListAltIcon style={{ color: "#2563EB" }} />
              {t("test.danhsachcauhoi")}
            </Typography>

            <Box className={classes.progressBox}>
              <Box className={classes.progressStats}>
                <span>{t("test.tiendolambai")}</span>
                <span>{answeredCount} / {totalQuestions} {t("test.cau")?.toLowerCase()}</span>
              </Box>
              <LinearProgress
                variant="determinate"
                value={progressPercent}
                style={{ height: 8, borderRadius: 4, backgroundColor: "#E2E8F0" }}
              />
            </Box>

            {/* Question Number Grid */}
            <Box className={classes.questionGrid}>
              {detailTest?.exam?.examQuestions?.map((_: any, index: number) => {
                const answered = isQuestionAnswered(index);
                const isActive = activeQuestionIndex === index;

                return (
                  <div
                    key={index}
                    className={clsx(
                      classes.questionBadge,
                      answered ? classes.questionBadgeAnswered : classes.questionBadgeUnanswered,
                      isActive && classes.questionBadgeActive
                    )}
                    onClick={() => handleSelectQuestion(index)}
                  >
                    {index + 1}
                  </div>
                );
              })}
            </Box>

            {/* Legend */}
            <Box className={classes.legendBox}>
              <Box className={classes.legendItem}>
                <Box className={classes.legendDot} style={{ backgroundColor: "#10BCA7" }} />
                <span>{t("test.dalam")} ({answeredCount})</span>
              </Box>
              <Box className={classes.legendItem}>
                <Box className={classes.legendDot} style={{ backgroundColor: "#CBD5E1" }} />
                <span>{t("test.chualam")} ({totalQuestions - answeredCount})</span>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Right Exam Questions Column */}
        <Grid item xs={12} md={8} lg={9}>
          {detailTest?.exam?.examQuestions?.map((question: ExamQuestions, index: number) => {
            const answered = isQuestionAnswered(index);
            return (
              <Paper
                key={question.id}
                id={`question-card-${index}`}
                className={classes.examCard}
                elevation={0}
              >
                <Box className={classes.questionNumberHeader}>
                  <Box className={classes.questionNumberBadge}>
                    {t("test.cau")} {index + 1}
                  </Box>
                  <Typography className={classes.questionContent}>
                    {question.question.content}
                  </Typography>
                </Box>

                {question.question.imageFileList.length > 0 && (
                  <Box
                    display="grid"
                    gridTemplateColumns={
                      question?.question?.imageFileList?.length === 1 ? "1fr" : "1fr 1fr"
                    }
                    gridGap={12}
                    my="16px"
                  >
                    {question.question.imageFileList.map((x: any) => (
                      <img
                        key={x.id}
                        style={{
                          width: "100%",
                          height: "fit-content",
                          objectFit: "contain",
                          maxHeight: "450px",
                          borderRadius: 8,
                          border: "1px solid #E2E8F0",
                        }}
                        src={x.imageFile.url}
                        alt="Question illustration"
                      />
                    ))}
                  </Box>
                )}

                {detailTest?.exam?.type === "TYPING" ? (
                  <TextField
                    autoComplete="off"
                    name="typing"
                    label={t("test.nhapdapan")}
                    rows={3}
                    variant="outlined"
                    fullWidth
                    multiline
                    value={getAnswerForQuestion(question.questionId, index)?.typing || ""}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      handleChangeOptionValue(event.target.value, question.questionId, index)
                    }
                  />
                ) : (
                  <RadioGroup name={`choice-${index}`}>
                    <Box className={classes.formAnswers}>
                      {question?.question?.answers?.map((answer) => {
                        const currentAns = getAnswerForQuestion(question.questionId, index);
                        const isSelected = Number(currentAns?.choice) === Number(answer.id);
                        return (
                          <Box
                            key={answer.id}
                            className={clsx(
                              classes.answerOptionBox,
                              isSelected && classes.answerOptionBoxSelected
                            )}
                            onClick={() => {
                              const syntheticEvent = {
                                target: { value: String(answer.id) },
                              } as React.ChangeEvent<HTMLInputElement>;
                              handleChangeRadio(syntheticEvent, question.questionId, index);
                            }}
                          >
                            <Radio
                              color="primary"
                              checked={isSelected}
                              value={answer.id}
                              onChange={(event) =>
                                handleChangeRadio(event, question.questionId, index)
                              }
                            />
                            <Typography className={classes.answerContent}>
                              {answer.content}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </RadioGroup>
                )}
              </Paper>
            );
          })}

          <Box textAlign="center" my={4} id="footer">
            <Button
              className={classes.btnSubmit}
              disabled={disabledSubmit}
              onClick={handleSubmitExam}
            >
              {t("test.nopbai")}
            </Button>
          </Box>
        </Grid>
      </Grid>

      {/* Floating Scroll Actions */}
      <Box className={classes.iconScroll}>
        <Tooltip title={t("test.arrow.dichuyenlendautrang") || "Lên đầu trang"}>
          <Box className={classes.scrollFab} onClick={handleScrollToTop}>
            <ExpandLessIcon />
          </Box>
        </Tooltip>
        <Tooltip title={t("test.arrow.dichuyenxuongcuoitrang") || "Xuống cuối trang"}>
          <Box className={classes.scrollFab} onClick={handleScrollToBottom}>
            <ExpandMoreIcon />
          </Box>
        </Tooltip>
      </Box>

      {/* Timeout Modal */}
      <Modal
        open={openModal}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        closeAfterTransition
        className={classes.rootModal}
      >
        <Slide direction="up" in={openModal}>
          <Box className={classes.modalContainer}>
            <Box className={classes.modalHeader}>
              <Typography variant="h6" style={{ fontWeight: 700 }}>
                Hết giờ làm bài
              </Typography>
              <IconButton disabled={disabledSubmit} onClick={handleSubmitExam} style={{ color: "#FFF" }}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Box className={classes.modalContent}>
              <Typography variant="body1" style={{ color: "#475569", marginBottom: 20 }}>
                {t("test.dahetgiolambai")}
              </Typography>
              <Button
                className={classes.btnSubmit}
                disabled={disabledSubmit}
                onClick={handleSubmitExam}
              >
                {t("test.nopbai")}
              </Button>
            </Box>
          </Box>
        </Slide>
      </Modal>

      {/* Modal Cảnh Báo Rời Tab / Chuyển Cửa Sổ */}
      <Modal
        open={openViolationModal}
        onClose={() => setOpenViolationModal(false)}
        aria-labelledby="violation-modal-title"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backdropFilter: "blur(8px)",
          backgroundColor: "rgba(15, 23, 42, 0.75)",
          zIndex: 9999,
        }}
      >
        <Paper
          style={{
            maxWidth: 480,
            width: "90%",
            borderRadius: 24,
            padding: "32px 24px",
            textAlign: "center",
            backgroundColor: "#FFFFFF",
            boxShadow: "0 25px 50px -12px rgba(220, 38, 38, 0.35)",
            border: "2px solid #EF4444",
            outline: "none",
          }}
        >
          <Box
            style={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              backgroundColor: "#FEE2E2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px auto",
              boxShadow: "0 0 0 10px rgba(254, 226, 226, 0.5)",
            }}
          >
            <WarningIcon style={{ fontSize: 42, color: "#DC2626" }} />
          </Box>
          <Typography
            id="violation-modal-title"
            variant="h6"
            style={{
              fontWeight: 900,
              color: "#991B1B",
              fontSize: "1.25rem",
              marginBottom: 12,
              letterSpacing: "-0.01em",
            }}
          >
            ⚠️ CẢNH BÁO VI PHẠM QUY CHẾ THI
          </Typography>
          <Typography
            variant="body1"
            style={{
              color: "#334155",
              fontSize: "0.95rem",
              lineHeight: 1.6,
              marginBottom: 16,
            }}
          >
            Hệ thống ghi nhận bạn vừa chuyển tab hoặc rời khỏi màn hình làm bài thi.
          </Typography>
          <Box
            style={{
              backgroundColor: "#FEF2F2",
              borderRadius: 12,
              padding: "12px 16px",
              marginBottom: 24,
              border: "1px dashed #FCA5A5",
            }}
          >
            <Typography style={{ color: "#B91C1C", fontWeight: 700, fontSize: "0.95rem" }}>
              Số lần vi phạm đã ghi nhận:{" "}
              <span style={{ fontSize: "1.3rem", fontWeight: 900, color: "#DC2626" }}>
                {violationCount}
              </span>
            </Typography>
            <Typography variant="caption" style={{ color: "#7F1D1D", display: "block", marginTop: 4 }}>
              Hành vi này đã được gửi trực tiếp đến Giám thị/Admin theo thời gian thực.
            </Typography>
          </Box>
          <Button
            variant="contained"
            fullWidth
            onClick={() => setOpenViolationModal(false)}
            style={{
              backgroundColor: "#DC2626",
              color: "#FFFFFF",
              fontWeight: 800,
              fontSize: "0.95rem",
              borderRadius: 12,
              padding: "12px 20px",
              textTransform: "none",
              boxShadow: "0 4px 14px rgba(220, 38, 38, 0.4)",
              transition: "all 0.2s ease",
            }}
          >
            Tôi Đã Hiểu & Tiếp Tục Làm Bài
          </Button>
        </Paper>
      </Modal>
      </Box>
    </div>
  );
}
